import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { DriverGroupDto } from '@cargovibe/shared-types';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useDeleteDriverGroup } from '@/modules/driver-groups/hooks/useDriverGroups';

interface Props {
  group: DriverGroupDto | null;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteDriverGroupDialog({
  group,
  onClose,
  onDeleted,
}: Readonly<Props>): React.JSX.Element {
  const { t } = useTranslation();
  const deleteMutation: ReturnType<typeof useDeleteDriverGroup> = useDeleteDriverGroup();

  const handleDelete = (): void => {
    if (!group) return;
    deleteMutation.mutate(group.id, { onSuccess: onDeleted });
  };

  const handleOpenChange = (open: boolean): void => {
    if (!open) onClose();
  };

  return (
    <Dialog open={!!group} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('driverGroups.deleteGroup')}</DialogTitle>
          <DialogDescription>
            {t('driverGroups.deleteGroupConfirm')} <strong>{group?.name}</strong>{' '}
            {t('driverGroups.deleteGroupDescription')}
          </DialogDescription>
        </DialogHeader>

        {deleteMutation.isError && (
          <p role="alert" className="text-sm text-destructive">
            {t('common.error')}: {deleteMutation.error.message}
          </p>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
